/**
 * Consent diagnostic panel.
 *
 * Loaded ONLY for tabs carrying the consent_debug sessionStorage flag (see the
 * inline loader in theme.liquid), so regular visitors never download it.
 * Activate with ?consent_debug=1 on any URL -- persists for the tab, survives
 * navigation, clears when the tab/incognito window closes. Deactivate with
 * ?consent_debug=0. privacy-opt-out.js owns that param and its own console
 * logging; this file only adds the on-page panel and checks below.
 *
 * Reads Shopify.customerPrivacy, the consent cookie and the gtag dataLayer
 * directly, then checks them against what the theme SHOULD be doing under the
 * global native-banner setup (every visitor starts blocked):
 *
 *   - Banner: visible exactly when the visitor has no decision on record.
 *   - Google: dataLayer's effective consent matches Shopify's decision,
 *     never granted while locally opted out (GPC / privacy tag / stored).
 *   - Stale: on Back/Forward (bfcache restore or a history load rebuilt from
 *     the HTTP cache) this page's consent record matches the latest decision,
 *     or the page reloads (privacy-opt-out.js's reloadIfConsentStale).
 *     Compared independently here, so a bug in that method can't hide itself.
 */
(function () {
  'use strict';

  var LATEST_RECORD_KEY = 'consent_latest_record'; // written by privacy-opt-out.js
  var STALE_RELOAD_KEY = 'consent_debug_stale_reload'; // ditto, before a stale reload
  var STALE_RELOAD_WINDOW_MS = 10000;
  var STALE_FAIL_AFTER_MS = 2500;
  // Keep in sync with wait_for_update in snippets/gtag-consent-defaults.liquid.
  var GTAG_WAIT_FOR_UPDATE_MS = 5000;

  function deepFind(selector, root) {
    var el = root.querySelector(selector);
    if (el) return el;
    var nodes = root.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].shadowRoot) {
        var found = deepFind(selector, nodes[i].shadowRoot);
        if (found) return found;
      }
    }
    return null;
  }

  function safeCall(cp, method) {
    if (!cp || typeof cp[method] !== 'function') return 'n/a';
    try {
      return cp[method]();
    } catch (e) {
      return 'error: ' + e.message;
    }
  }

  function isVisible(el) {
    if (!el) return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function readConsentCookie() {
    try {
      return document.cookie.split('; ')
        .find(function (c) { return c.indexOf('_tracking_consent=') === 0; }) || '';
    } catch (e) {
      return '';
    }
  }

  function readStoredOptOut() {
    try { return localStorage.getItem('privacy_opt_out'); } catch (e) { return null; }
  }

  // Same normalization as privacy-opt-out.js's readShopifyRecord(), recomputed
  // here on purpose. null while the API isn't available.
  function readPageRecord() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.currentVisitorConsent !== 'function') return null;
    try {
      var c = cp.currentVisitorConsent() || {};
      return JSON.stringify([c.marketing, c.analytics, c.preferences, c.sale_of_data]);
    } catch (e) {
      return null;
    }
  }

  function readLatestRecord() {
    try { return localStorage.getItem(LATEST_RECORD_KEY); } catch (e) { return null; }
  }

  // Replays every gtag('consent', ...) command queued so far, in order, to get
  // the effective Consent Mode state Google is working from on this page.
  function googleConsentState() {
    var state = {};
    var entries = window.dataLayer || [];
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry || entry[0] !== 'consent' || !entry[2]) continue;
      Object.keys(entry[2]).forEach(function (key) {
        if (key !== 'wait_for_update') state[key] = entry[2][key];
      });
    }
    return state;
  }

  // When gtag.js finished downloading, in ms since navigation start -- Google's
  // wait_for_update window opens about then. null if it hasn't loaded.
  function gtagLoadedAt() {
    try {
      var entry = performance.getEntriesByType('resource').filter(function (r) {
        return r.name.indexOf('googletagmanager.com/gtag/js') !== -1;
      })[0];
      return entry ? Math.round(entry.responseEnd) : null;
    } catch (e) {
      return null;
    }
  }

  function navigationType() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      return nav ? nav.type : 'n/a';
    } catch (e) {
      return 'n/a';
    }
  }

  // --- Back/Forward staleness tracking --------------------------------------
  var navType = navigationType();
  var lastPageshow = 'none yet';
  var reloadNote = 'none this page';

  // A stale page reloads, which wipes its state -- privacy-opt-out.js leaves a
  // breadcrumb first so the reloaded page can report it.
  try {
    var crumb = JSON.parse(sessionStorage.getItem(STALE_RELOAD_KEY) || 'null');
    sessionStorage.removeItem(STALE_RELOAD_KEY);
    var crumbIsForThisLoad = crumb && crumb.href === location.href &&
      Date.now() - crumb.at < STALE_RELOAD_WINDOW_MS;
    if (crumbIsForThisLoad) {
      reloadNote = navType === 'reload'
        ? 'PASS: reloaded a stale page (' + crumb.trigger + ')'
        : 'stale reload requested (' + crumb.trigger + ') but nav=' + navType;
    }
  } catch (e) {}

  // On a bfcache restore this page's state is what it was at pagehide, so
  // that's the record to compare (matches privacy-opt-out.js).
  var recordAtHide = null;
  var restoredFromBfcache = false;
  window.addEventListener('pagehide', function (e) {
    recordAtHide = readPageRecord();
    try { console.log('[CONSENT DEBUG] pagehide, entering bfcache:', e.persisted); } catch (err) {}
  });

  var staleSince = null;

  // --- Snapshot + expectations ----------------------------------------------
  function snapshot() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    var bannerEl = deepFind('#shopify-pc__banner, .shopify-pc__banner', document);
    var declineEl = deepFind('.shopify-pc__banner__btn-decline', document);
    var consent = safeCall(cp, 'currentVisitorConsent');
    var google = googleConsentState();
    var privacy = window.__customerPrivacy || {};
    var localOptOut = !!(window.PrivacyOptOut && window.PrivacyOptOut.isOptedOut);

    var d = {
      time: new Date().toISOString(),
      href: location.href,
      apiReady: !!cp,
      region: safeCall(cp, 'getRegion'),
      saleOfDataRegion: safeCall(cp, 'saleOfDataRegion'),
      analyticsAllowed: safeCall(cp, 'analyticsProcessingAllowed'),
      marketingAllowed: safeCall(cp, 'marketingAllowed'),
      saleOfDataAllowed: safeCall(cp, 'saleOfDataAllowed'),
      currentVisitorConsent: consent,
      consentCookiePresent: readConsentCookie() !== '',
      bannerInDom: !!bannerEl,
      bannerVisible: isVisible(bannerEl),
      declineVisible: isVisible(declineEl),
      localOptOut: localOptOut,
      gpc: navigator.globalPrivacyControl === true,
      privacyTag: privacy.hasPrivacyTag === true,
      storedOptOut: readStoredOptOut(),
      google: google,
      trackingEnabled: window.TrackingManager ? window.TrackingManager.isTrackingEnabled() : 'n/a',
      fbqType: typeof window.fbq,
      yotpoConversionOff: window.yotpo ? window.yotpo.disableConversionTracking : 'n/a',
      noticeScriptPresent: !!document.querySelector('script[src*="notice-consent-banner"]'),
      legacyThemeBannerInDom: !!document.querySelector('.cookie_consent'),
      navType: navType,
      lastPageshow: lastPageshow,
      reloadNote: reloadNote,
      pageRecord: restoredFromBfcache ? recordAtHide : readPageRecord(),
      latestRecord: readLatestRecord()
    };

    // Stale: this page's consent is older than the latest decision. Only Back/
    // Forward pages are expected to be reloaded for it; on a normal load
    // privacy-opt-out.js overwrites the latest record with this page's (a
    // moment after the API loads), so a brief STALE there is normal.
    var mayBeStale = navType === 'back_forward' || restoredFromBfcache;
    var canCompare = d.pageRecord !== null && d.latestRecord !== null;
    var isStale = canCompare && d.pageRecord !== d.latestRecord;
    if (!isStale) staleSince = null;
    else if (staleSince === null) staleSince = Date.now();
    var staleTooLong = isStale && Date.now() - staleSince > STALE_FAIL_AFTER_MS;
    d.staleCheck = !canCompare ? 'n/a (no record yet)'
      : !isStale ? 'OK'
      : !staleTooLong ? 'STALE (reload expected' + (mayBeStale ? '' : ' only on Back/Forward') + ')'
      : mayBeStale ? 'FAIL: stale Back/Forward page did NOT reload'
      : 'MISMATCH: fresh load never recorded itself as latest';

    // Banner: should show exactly while undecided. Shopify injects it a beat
    // after the API is ready, so a brief MISMATCH right after load is normal.
    var hasDecision = !!consent && typeof consent === 'object' &&
      ['marketing', 'analytics'].some(function (k) {
        return consent[k] === 'yes' || consent[k] === 'no';
      });
    d.hasDecision = hasDecision;
    d.bannerCheck = !d.apiReady ? 'n/a (API not ready)'
      : d.bannerVisible === !hasDecision ? 'OK'
      : 'MISMATCH: expected banner ' + (hasDecision ? 'hidden' : 'visible');
    d.declineCheck = !d.bannerVisible ? 'n/a (no banner)'
      : d.declineVisible ? 'OK' : 'MISMATCH: banner up without Decline';

    // Google: granted only when Shopify allows the category AND nothing local
    // says opted out. tracking-manager.js grants once the API is ready, so a
    // brief MISMATCH right after load (before that) is normal.
    var expectAnalytics = d.analyticsAllowed === true && !localOptOut ? 'granted' : 'denied';
    var expectAds = d.marketingAllowed === true && !localOptOut ? 'granted' : 'denied';
    var analyticsOk = google.analytics_storage === expectAnalytics;
    var adsOk = google.ad_storage === expectAds;
    d.googleCheck = analyticsOk && adsOk ? 'OK'
      : 'MISMATCH: expected analytics=' + expectAnalytics + ' ads=' + expectAds;

    // Grant timing: for a consenting visitor, did our grant land inside
    // Google's wait_for_update window? If not, this page's first hits were
    // already sent unconsented and won't be resent.
    var expectsGrant = expectAnalytics === 'granted' || expectAds === 'granted';
    var grantAt = window.TrackingManager ? window.TrackingManager.firstGrantAt : null;
    var grantTrigger = window.TrackingManager ? window.TrackingManager.firstGrantTrigger : null;
    var gtagAt = gtagLoadedAt();
    d.grantTiming = 'grant @ ' + grantAt + 'ms (' + grantTrigger + '), gtag.js @ ' + gtagAt +
      'ms, window ' + GTAG_WAIT_FOR_UPDATE_MS + 'ms';
    if (!expectsGrant) {
      d.grantCheck = 'n/a (nothing to grant)';
    } else if (grantTrigger === 'decision') {
      // Undecided until the click, so hits before it were correctly unconsented.
      d.grantCheck = 'n/a (decided on this page)';
    } else if (grantAt === null) {
      d.grantCheck = 'pending';
    } else if (gtagAt === null || grantAt <= gtagAt) {
      d.grantCheck = 'OK (granted before gtag.js loaded)';
    } else {
      var margin = gtagAt + GTAG_WAIT_FOR_UPDATE_MS - grantAt;
      d.grantCheck = margin >= 0
        ? 'OK (' + margin + 'ms to spare)'
        : 'LATE by ' + -margin + 'ms: first hits went out unconsented';
    }

    d.noticeCheck = d.noticeScriptPresent ? 'MISMATCH: removed notice script is loading' : 'OK';
    return d;
  }

  // --- Panel ----------------------------------------------------------------
  var panel = document.createElement('div');
  panel.id = 'consent-debug-panel';
  panel.style.cssText = 'position:fixed;top:0;right:0;bottom:auto;z-index:2147483647;' +
    'background:#000;color:#0f0;font:11px/1.5 monospace;padding:8px 10px;' +
    'max-width:440px;max-height:100vh;overflow:hidden;white-space:pre-wrap;' +
    // Long JSON values have no spaces for pre-wrap to break at.
    'overflow-wrap:anywhere;' +
    'pointer-events:none;opacity:0.94;border-bottom-left-radius:6px;';

  function attachPanel() {
    if (panel.isConnected) return;
    (document.body || document.documentElement).appendChild(panel);
  }

  var lastSerialized = null;
  var pollCount = 0;
  var observer = null;

  // Writing the panel is itself a DOM mutation inside the subtree the observer
  // watches, so the observer is disconnected around the write -- otherwise it
  // re-fires on its own update and loops forever (starving the page, including
  // Shopify's banner render).
  function render(reason) {
    attachPanel();
    var d = snapshot();

    if (observer) observer.disconnect();
    panel.textContent =
      'CONSENT DEBUG  (?consent_debug=0 to hide)\n' +
      'updated: ' + d.time.split('T')[1] + '  (' + reason + ', poll ' + pollCount + ')\n' +
      '\n-- checks --\n' +
      'banner:  ' + d.bannerCheck + '\n' +
      'decline: ' + d.declineCheck + '\n' +
      'google:  ' + d.googleCheck + '\n' +
      'grant:   ' + d.grantCheck + '\n' +
      'notice:  ' + d.noticeCheck + '\n' +
      'stale:   ' + d.staleCheck + '\n' +
      'reload:  ' + d.reloadNote + '\n' +
      '\n-- shopify --\n' +
      'apiReady: ' + d.apiReady + '  region: ' + d.region + '  saleOfDataRegion: ' + d.saleOfDataRegion + '\n' +
      'analyticsAllowed: ' + d.analyticsAllowed + '  marketingAllowed: ' + d.marketingAllowed +
        '  saleOfDataAllowed: ' + d.saleOfDataAllowed + '\n' +
      'currentVisitorConsent: ' + JSON.stringify(d.currentVisitorConsent) + '\n' +
      'hasDecision: ' + d.hasDecision + '  _tracking_consent cookie: ' + d.consentCookiePresent + '\n' +
      'banner inDom: ' + d.bannerInDom + '  visible: ' + d.bannerVisible + '  decline visible: ' + d.declineVisible + '\n' +
      '\n-- local --\n' +
      'PrivacyOptOut.isOptedOut: ' + d.localOptOut + '  GPC: ' + d.gpc + '  privacyTag: ' + d.privacyTag + '\n' +
      'privacy_opt_out: ' + d.storedOptOut + '\n' +
      '\n-- google / trackers --\n' +
      'consent: ' + JSON.stringify(d.google) + '\n' +
      'timing: ' + d.grantTiming + '\n' +
      'trackingEnabled: ' + d.trackingEnabled + '  fbq: ' + d.fbqType + '  yotpoConversionOff: ' + d.yotpoConversionOff + '\n' +
      '\n-- navigation --\n' +
      'navType: ' + d.navType + '  lastPageshow: ' + d.lastPageshow + '\n' +
      'page record:   ' + d.pageRecord + '\n' +
      'latest record: ' + d.latestRecord + '\n' +
      'legacyThemeBannerInDom: ' + d.legacyThemeBannerInDom;
    if (observer) observer.observe(document.documentElement, { childList: true, subtree: true });

    // Console: dedupe identical snapshots so polling stays quiet. `time` and
    // `href` are excluded -- time always differs, and a real navigation re-runs
    // this whole script anyway.
    var comparable = Object.assign({}, d);
    delete comparable.time;
    delete comparable.href;
    var serialized = JSON.stringify(comparable);
    if (serialized === lastSerialized && reason !== 'initial') return;
    lastSerialized = serialized;
    console.log('[CONSENT DEBUG] (' + reason + ')', d);
  }

  window.addEventListener('pageshow', function (e) {
    lastPageshow = 'persisted=' + e.persisted + ' @ ' + new Date().toISOString().split('T')[1];
    restoredFromBfcache = e.persisted;
    if (e.persisted) reloadNote = 'restored from bfcache (no reload = record matched)';
    render(e.persisted ? 'pageshow (bfcache restore)' : 'pageshow');

    // Re-render after the fail window so a stale page that never reloaded
    // flips to FAIL even if nothing else triggers a render (poll has ended).
    setTimeout(function () { render('stale check'); }, STALE_FAIL_AFTER_MS + 100);
  });

  document.addEventListener('visitorConsentCollected', function () {
    render('visitorConsentCollected');
  });

  render('initial');

  var timer = setInterval(function () {
    pollCount += 1;
    render('poll');
    if (pollCount >= 30) clearInterval(timer); // ~15s, then rely on the observer below
  }, 500);

  if (typeof MutationObserver === 'function') {
    observer = new MutationObserver(function () { render('dom mutation'); });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
